import { StandardEvents } from '@shopify/events';

// PDP prices are supplied by groovi-live-price.js, the same source as installments
// and unit prices. Cart totals are read only after successful cart mutations.
let latestCartTotal = null;
let requestId = 0;
let celebrationAsset;

class GrooviFreeship extends HTMLElement {
  static observedAttributes = ['data-product-total', 'data-cart-total', 'data-threshold', 'data-before', 'data-reached', 'data-product-before', 'data-projected', 'data-confirmed', 'data-calculation', 'data-currency', 'data-locale', 'data-confetti'];

  connectedCallback() {
    this.celebratedForGoal ??= false;
    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.75;
      this.checkCelebration();
    }, { threshold: [0, 0.75] });
    this.visibilityObserver.observe(this);
    this.lifecycle = new AbortController();
    const options = { signal: this.lifecycle.signal, capture: true, passive: true };
    for (const event of ['theme-drawer:open', 'theme-drawer:close', 'toggle', 'close', 'visibilitychange', 'scroll', 'animationend', 'transitionend']) {
      document.addEventListener(event, this.checkCelebration, options);
    }
    this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
    this.motionPreference.addEventListener('change', this.checkCelebration, options);
    queueMicrotask(() => { if (this.isConnected) this.render(); });
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    this.lifecycle?.abort();
    this.stopCelebration();
  }

  attributeChangedCallback() {
    // Section morphing can patch attributes before replacing the child markup.
    queueMicrotask(() => { if (this.isConnected) this.render(); });
  }

  render() {
    const threshold = Number(this.dataset.threshold);
    const message = this.querySelector('.groovi-freeship__message');
    const track = this.querySelector('.groovi-freeship__track');
    if (!(threshold > 0) || !message || !track) return;

    const isProduct = this.dataset.context === 'product';
    const cartTotal = Math.max(0, (isProduct ? latestCartTotal : null) ?? (Number(this.dataset.cartTotal) || 0));
    // Only the actual discounted cart can confirm the benefit. A PDP selection
    // is a possible NEW addition, even when this product is already in the cart.
    const reached = cartTotal >= threshold;
    let total = cartTotal;
    if (isProduct && !reached) {
      total = (Number(this.dataset.productTotal) || 0) + (this.dataset.calculation === 'product_cart' ? cartTotal : 0);
    }
    total = Math.max(0, total);
    const remaining = Math.max(0, threshold - total);
    const projected = isProduct && !reached && remaining === 0;
    const percent = Math.min(100, total * 100 / threshold);
    const money = (cents) => new Intl.NumberFormat(this.dataset.locale || 'pt-BR', {
      style: 'currency', currency: this.dataset.currency || 'BRL',
    }).format(cents / 100);
    let template = reached ? this.dataset.reached : this.dataset.before;
    if (isProduct) {
      template = reached ? this.dataset.confirmed : projected ? this.dataset.projected : this.dataset.productBefore;
    }
    const text = template.replaceAll('[valor]', money(remaining));

    if (message.textContent !== text) {
      if (!template.includes('[valor]')) message.textContent = text;
      else {
        const parts = template.split('[valor]');
        const nodes = [];
        parts.forEach((part, index) => {
          if (index) {
            const amount = document.createElement('strong');
            amount.textContent = money(remaining);
            nodes.push(amount);
          }
          nodes.push(document.createTextNode(part));
        });
        message.replaceChildren(...nodes);
      }
    }
    const amount = this.querySelector('[data-freeship-amount]');
    if (amount) amount.textContent = money(Math.min(total, threshold));
    this.style.setProperty('--gf-progress', `${percent}%`);
    this.classList.toggle('is-reached', reached);
    this.dataset.state = reached ? 'confirmed' : projected ? 'projected' : 'progress';
    track.setAttribute('aria-valuenow', String(Math.floor(percent)));
    track.setAttribute('aria-valuetext', text);

    this.checkCelebration();
  }

  isVisible() {
    if (!this.isConnected || !this.inView || document.hidden || this.motionPreference?.matches) return false;
    const drawer = this.closest('theme-drawer');
    if (drawer && !drawer.hasAttribute('open')) return false;
    const rect = this.getBoundingClientRect();
    // Hit-testing also defers the PDP burst while an auto-opened drawer covers it.
    const front = document.elementFromPoint(rect.x + rect.width / 2, rect.y + rect.height / 2);
    return !!front && (front === this || this.contains(front));
  }

  stopCelebration() {
    clearTimeout(this.celebrationTimer);
    this.celebrationLayer?.remove();
    this.celebrationLayer = null;
  }

  canCelebrate() {
    // On the PDP, a full projected bar celebrates the selection while the copy
    // still asks for the add. Cart surfaces require the actual cart to qualify.
    return this.dataset.confetti === 'true' && (
      this.dataset.state === 'confirmed' ||
      (this.dataset.context === 'product' && this.dataset.state === 'projected')
    );
  }

  checkCelebration = async () => {
    if (!this.canCelebrate()) {
      this.celebratedForGoal = false;
      this.stopCelebration();
      return;
    }
    // Leaving the viewport hides the effect without rearming this achievement.
    if (!this.isVisible()) {
      this.stopCelebration();
      return;
    }
    if (this.celebratedForGoal || this.preparingCelebration) return;
    this.preparingCelebration = true;
    try {
      // Wait for the drawer to settle, so the effect is anchored to its final position.
      const dialog = this.closest('dialog');
      await Promise.allSettled((dialog?.getAnimations() || []).map((animation) => animation.finished));
      celebrationAsset ||= fetch(this.dataset.celebrationSrc).then((response) => {
        if (!response.ok) throw new Error('Celebration asset unavailable');
        return response.text();
      }).catch((error) => { celebrationAsset = null; throw error; });
      const source = await celebrationAsset;
      if (!this.canCelebrate() || !this.isVisible() || this.celebratedForGoal) return;

      const layer = document.createElement('div');
      layer.className = 'groovi-freeship-celebration';
      layer.setAttribute('aria-hidden', 'true');
      layer.setAttribute('popover', 'manual');
      // Isolate the supplied SVG's animation names and mask IDs between instances.
      const shadow = layer.attachShadow({ mode: 'open' });
      const svg = new DOMParser().parseFromString(source, 'image/svg+xml').documentElement;
      const rect = this.getBoundingClientRect();
      const size = Math.min(rect.width + 96, innerWidth - 24, innerHeight - 24, 680);
      const x = Math.max(12, Math.min(rect.x + rect.width / 2 - size / 2, innerWidth - size - 12));
      const y = Math.max(12, Math.min(rect.y + rect.height / 2 - size / 2, innerHeight - size - 12));
      svg.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${size}px;height:${size}px;overflow:visible;pointer-events:none`;
      shadow.append(svg);
      document.body.append(layer);
      layer.showPopover?.();
      this.celebrationLayer = layer;
      this.celebratedForGoal = true;
      this.celebrationTimer = setTimeout(() => this.stopCelebration(), 1300);
    } catch {
      // An unavailable decorative asset must never interrupt shopping.
    } finally {
      this.preparingCelebration = false;
    }
  };
}

if (!customElements.get('groovi-freeship')) customElements.define('groovi-freeship', GrooviFreeship);

async function refreshCart() {
  if (!document.querySelector('groovi-freeship')) return;
  const id = ++requestId;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
    if (!response.ok) return;
    const cart = await response.json();
    if (id !== requestId || !Number.isFinite(cart.total_price)) return;
    latestCartTotal = cart.total_price;
    document.querySelectorAll('groovi-freeship').forEach((bar) => {
      bar.dataset.cartTotal = String(latestCartTotal);
    });
  } catch {
    // Retain the server-rendered value if the cart cannot be refreshed.
  }
}

for (const eventName of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
  document.addEventListener(eventName, (event) => {
    // Horizon dispatches the event before the request settles, including failures.
    // Never show an optimistic cart amount as an earned shipping benefit.
    if (event.promise) event.promise.then(refreshCart).catch(() => {});
  });
}
document.addEventListener('shopify:section:load', refreshCart);
window.addEventListener('pageshow', (event) => { if (event.persisted) refreshCart(); });
refreshCart();
